import PropTypes from 'prop-types';
import { generateServicesIntl, profileSettingsIntl } from './intl/index.intl';
import { SERVICE_CATEGORIES_NAMES } from './names/index.names';
import { GENERAL_SERVICES_NAMES } from './names/_generalServices.names';
import { VEHICLE_PURCHASE_SERVICES_NAMES } from './names/_vehiclePurchaseServices.names';
import { WORKSHOP_SERVICES_NAMES } from './names/_workshopServices.names';

/**
 * @typedef {import('./names/index.names').AllServicesNames} AllServicesNames
 * @typedef {import('./names/index.names').DealerProfileServiceCategoryKey} DealerProfileServiceCategoryKey
 * @typedef {{category: DealerProfileServiceCategoryKey, label: string, services: {label: string, name: string}[]}} DealerProfileServiceSettings
 * @typedef {{formatMessage: ({id, defaultMessage}: {id: string, defaultMessage: string}) => string}} IntlType
 */
/**
 * @description
 *
 * source for all dealer profile sevice translations
 *
 * @param {IntlType} intl
 * @returns {{Record<DealerProfileServiceCategoryKey, DealerProfileServiceSettings>}}
 */
export const DEALER_PROFILE_SERVICES_SETTINGS = intl => {
  const {
    GENERAL_SERVICES: GEN,
    VEHICLE_PURCHASE_SERVICES: VEH,
    WORKSHOP_SERVICES: WOR,
  } = SERVICE_CATEGORIES_NAMES;

  return {
    [GEN]: {
      category: GEN,
      label: intl.formatMessage(profileSettingsIntl[GEN]),
      services: generateServicesIntl(GEN)(intl),
    },
    [VEH]: {
      category: VEH,
      label: intl.formatMessage(profileSettingsIntl[VEH]),
      services: generateServicesIntl(VEH)(intl),
    },
    [WOR]: {
      category: WOR,
      label: intl.formatMessage(profileSettingsIntl[WOR]),
      services: generateServicesIntl(WOR)(intl),
    },
  };
};

/**
 * @description
 * zips an array, pairing the first element with the median value
 *
 * this is used for resorting items since they appear in different
 * orders on different breakpoits
 *
 * @param {{name: string, label: string}} list
 */
export const getZippedArrayItems = (list = []) => {
  const mathFn = list.length % 2 !== 0 ? Math.ceil : identity => identity;
  const listMid = mathFn(list.length / 2);
  const firstHalf = list.slice(0, listMid);
  const secondHalf = list.slice(listMid);

  return firstHalf.flatMap((e, i) => [e, secondHalf[i]]).filter(Boolean);
};

export const ALL_CATEGORIES = Object.values(SERVICE_CATEGORIES_NAMES);
/**
 * @description
 * list of all services names
 *
 * @const ALL_SERVICES_NAMES
 * @type {AllServicesNames}
 */
const ALL_SERVICES_NAMES = Object.values({
  ...GENERAL_SERVICES_NAMES,
  ...VEHICLE_PURCHASE_SERVICES_NAMES,
  ...WORKSHOP_SERVICES_NAMES,
});

/**
 * @description
 * total count for all services
 */
export const TOTAL_SERVICE_COUNT = ALL_SERVICES_NAMES.length;

/**
 * @description
 * union type for all service names
 */
export const DealerProfileServicesShape = PropTypes.oneOf(ALL_SERVICES_NAMES);
